/**
 * Starea comenzii, in cuvintele clientului.
 *
 * Statusurile din contract sunt vocabular intern (`awaiting_assignment`,
 * `committed`). Ce se afiseaza aici e ce intelege omul care asteapta mancarea;
 * codul intern nu apare pe ecran nici macar in paranteza.
 */

import { PLATFORM_ORDER_STATUSES, type OrderTimelineEntryDto, type PlatformOrderStatus, type RunStopStatus } from "@deliveryapp/shared";

import { formatClock } from "./EtaWindow";
import { Badge } from "./ui";

export const ORDER_STATUS_LABELS: Record<PlatformOrderStatus, string> = {
  quoting: "Comandă neconfirmată",
  committed: "Trimisă restaurantului",
  awaiting_assignment: "Se caută un curier",
  assigned: "Curier alocat",
  in_kitchen: "Se pregătește",
  ready: "Gata de ridicare",
  picked_up: "Preluată de curier",
  in_transit: "În drum spre tine",
  delivered: "Livrată",
  failed: "Nelivrată",
  cancelled: "Anulată",
};

const STATUS_TONES: Record<PlatformOrderStatus, "neutral" | "success" | "warning" | "danger"> = {
  quoting: "neutral",
  committed: "neutral",
  awaiting_assignment: "neutral",
  assigned: "neutral",
  in_kitchen: "warning",
  ready: "warning",
  picked_up: "warning",
  in_transit: "warning",
  delivered: "success",
  failed: "danger",
  cancelled: "danger",
};

const STOP_STATUS_LABELS: Record<RunStopStatus, string> = {
  pending: "Programată",
  en_route: "Curierul se deplasează",
  arrived: "Curierul a ajuns",
  completed: "Finalizată",
  failed: "Nefinalizată",
  refused: "Refuzată la ușă",
};

/**
 * Un status necunoscut inseamna ca serverul e mai nou decat acest bundle. Se
 * afiseaza neutru, fara sa pretindem ca stim ce s-a intamplat.
 */
export function orderStatusLabel(status: string): string {
  return (PLATFORM_ORDER_STATUSES as readonly string[]).includes(status)
    ? ORDER_STATUS_LABELS[status as PlatformOrderStatus]
    : "Stare în curs de actualizare";
}

/** La ridicare nu exista curier: aceleasi stari, spuse din perspectiva ghiseului. */
const PICKUP_STATUS_LABELS: Partial<Record<PlatformOrderStatus, string>> = {
  ready: "Gata — te așteptăm",
  picked_up: "Ridicată",
  delivered: "Ridicată",
  failed: "Nepredată",
};

export function OrderStatusBadge({ status, pickup = false }: { status: string; pickup?: boolean }) {
  const tone = (PLATFORM_ORDER_STATUSES as readonly string[]).includes(status)
    ? pickup && status === "picked_up"
      ? "success"
      : STATUS_TONES[status as PlatformOrderStatus]
    : "neutral";
  const label = (pickup ? PICKUP_STATUS_LABELS[status as PlatformOrderStatus] : undefined) ?? orderStatusLabel(status);
  return <Badge tone={tone}>{label}</Badge>;
}

export function stopStatusLabel(status: string): string {
  const table: Record<string, string | undefined> = STOP_STATUS_LABELS;
  return table[status] ?? "Stare în curs de actualizare";
}

export function OrderTimeline({
  entries,
  timezone,
}: {
  entries: OrderTimelineEntryDto[];
  timezone: string;
}) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-ink-muted">
        Nu există încă evenimente înregistrate pentru această comandă.
      </p>
    );
  }

  return (
    <ol className="flex flex-col gap-3">
      {entries.map((entry, index) => (
        <li key={`${entry.at}-${entry.status}-${index}`} className="flex gap-3">
          <span aria-hidden="true" className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">{orderStatusLabel(entry.status)}</p>
            <p className="text-xs text-ink-muted">{formatClock(entry.at, timezone)}</p>
            {entry.note === null || entry.note === undefined || entry.note.length === 0 ? null : (
              <p className="mt-0.5 text-sm text-ink-muted">{entry.note}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
